import { inject, Injectable } from '@angular/core'
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router'

import { ApiService } from '@ti/core/api'

import { AuthSession, LogoutStrategy } from '../services'
import { parseInternalReturnUrl } from '../utils/internal-return-url'

@Injectable({
  providedIn: 'root',
})
export class AuthGuardService {
  protected readonly api = inject(ApiService)
  protected readonly authSession = inject(AuthSession)
  protected readonly logoutStrategy = inject(LogoutStrategy)
  protected readonly router = inject(Router)

  async isLoggedIn(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Promise<boolean | UrlTree> {
    if (await this.authSession.isLoggedIn()) {
      const isActive = await this.checkIsUserActive()
      if (!isActive) {
        await this.logoutStrategy.signOut()
        return false
      }
      return true
    } else {
      this.router.navigate(['/sign-in'], { queryParams: { returnUrl: state.url } })
      return false
    }
  }

  async notLoggedIn(
    route: ActivatedRouteSnapshot,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    state: RouterStateSnapshot,
  ) {
    if (await this.authSession.isLoggedIn()) {
      // The session check can flap: a visitor sent here because it failed once may pass it now.
      // Such a visitor belongs where the sign-in was meant to lead, not on the root.
      const returnUrl = parseInternalReturnUrl(route.queryParams['returnUrl'])
      if (returnUrl) {
        this.router.navigateByUrl(returnUrl)
      } else {
        this.router.navigate(['/'])
      }
      return false
    } else {
      return true
    }
  }

  protected async checkIsUserActive() {
    try {
      const isConnected = await this.authSession.isLoggedIn()
      if (!isConnected) {
        // If we are offline and reached this point, we assume the user is active
        // because we can't verify with the server.
        return true
      }
      return this.api.myProfile.isActive()
    } catch (e) {
      console.error(`AuthGuard::checkIsUserActive`, e)
    }
    return false
  }
}

export const loggedInGuard: CanActivateFn = async (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
) => {
  return inject(AuthGuardService).isLoggedIn(route, state)
}

export const notLoggedInGuard: CanActivateFn = async (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
) => {
  return inject(AuthGuardService).notLoggedIn(route, state)
}
